import { useState, useEffect } from "react";

const useMarkups = (markupData) => {
  const [markups, setMarkups] = useState(markupData);

  useEffect(() => {
    setMarkups(markupData);
  }, [markupData]);

  // const interpolate = (x, x0, x1, y0, y1) => {
  //   return y0 + ((x - x0) * (y1 - y0)) / (x1 - x0);
  // };
  /*
  By converting the markups to discounts, interpolating the discounts linearly, and then
  converting back to markups, we respect the non-linear relationship between discount and markup.
  This method ensures that the interpolation behaves correctly across the entire range.
  https://chatgpt.com/share/67008a71-2a8c-8005-b1b5-6c169e547153
  */
  const interpolate = (x, x0, x1, y0, y1) => {
    if (x === x0) return y0;
    if (x === x1) return y1;

    // Step 1: Convert markups to discounts
    const discount0 = 1 - 1 / y0;
    const discount1 = 1 - 1 / y1;

    // Step 2: Linearly interpolate the discounts
    const discount =
      discount0 + ((x - x0) * (discount1 - discount0)) / (x1 - x0);

    // Step 3: Calculate the markup from the interpolated discount
    const markup = 1 / (1 - discount);

    return markup;
  };

  const findClosestEntries = (discount) => {
    if (discount <= markups[0].discount) {
      return { lower: markups[0], upper: markups[0] };
    }
    if (discount >= markups[markups.length - 1].discount) {
      return {
        lower: markups[markups.length - 1],
        upper: markups[markups.length - 1],
      };
    }

    for (let i = 0; i < markups.length - 1; i++) {
      if (
        markups[i].discount <= discount &&
        markups[i + 1].discount >= discount
      ) {
        return { lower: markups[i], upper: markups[i + 1] };
      }
    }

    return {
      lower: markups[0],
      upper: markups[markups.length - 1],
    }; // Fallback case
  };

  const calcEndUserMarkup = (discount, vendor = null) => {
    // Check for vendor override first
    if (vendor?.EndUserMarkup != null) {
      return vendor.EndUserMarkup;
    }
    
    if (!discount) discount = 0; // Ensure numeric value

    const { lower, upper } = findClosestEntries(discount);

    const markup =
      discount <= lower.discount
        ? lower.endUserMarkup
        : discount >= upper.discount
        ? upper.endUserMarkup
        : interpolate(
            discount,
            lower.discount,
            upper.discount,
            lower.endUserMarkup,
            upper.endUserMarkup
          );

    return markup;
  };

  const calcDealerMarkup = (discount, vendor = null) => {
    // Check for vendor override first
    if (vendor?.DealerMarkup != null) {
      return vendor.DealerMarkup;
    }
    
    if (!discount) discount = 0; // Ensure numeric value

    const { lower, upper } = findClosestEntries(discount);

    const markup =
      discount <= lower.discount
        ? lower.dealerMarkup
        : discount >= upper.discount
        ? upper.dealerMarkup
        : interpolate(
            discount,
            lower.discount,
            upper.discount,
            lower.dealerMarkup,
            upper.dealerMarkup
          );
    return markup;
  };

  const calcWholesalerMarkup = (discount, vendor = null) => {
    // Check for vendor override first
    if (vendor?.WholesalerMarkup != null) {
      return vendor.WholesalerMarkup;
    }
    
    if (!discount) discount = 0; // Ensure numeric value

    const { lower, upper } = findClosestEntries(discount);

    const markup =
      discount <= lower.discount
        ? lower.wholesalerMarkup
        : discount >= upper.discount
        ? upper.wholesalerMarkup
        : interpolate(
            discount,
            lower.discount,
            upper.discount,
            lower.wholesalerMarkup,
            upper.wholesalerMarkup
          );
    return markup;
  };

  const calcEndUserPrice = (list, discount, vendor = null) => {
    const endUserMarkup = calcEndUserMarkup(discount, vendor);
    const cost = calcCost(list, discount);
    //const endUserPrice = Math.max(list, cost * endUserMarkup);
    const endUserPrice = cost * endUserMarkup;
    return endUserPrice.toFixed(2);
  };

  const calcDealerPrice = (list, discount, vendor = null) => {
    const dealerMarkup = calcDealerMarkup(discount, vendor);
    const cost = calcCost(list, discount);
    const dealerPrice = cost * dealerMarkup;
    return dealerPrice.toFixed(2);
  };

  const calcWholesalerPrice = (list, discount, vendor = null) => {
    const wholesalerMarkup = calcWholesalerMarkup(discount, vendor);
    const cost = calcCost(list, discount);
    const wholesalerPrice = cost * wholesalerMarkup;
    return wholesalerPrice.toFixed(2);
  };

  const calcCost = (list, discount) => {
    const discountRate = 1 - parseFloat(discount);
    return list * discountRate;
  };

  const calcListPrice = (cost, discount) => {
    const discountRate = 1 - parseFloat(discount);
    if (discountRate === 0) {
      return cost;
      //throw new Error("Discount rate cannot be zero");
    }
    return cost / discountRate;
  };

  const calcMargin = (discount) => {
    const margin = 1 / (1 - parseFloat(discount)) - 1;
    return margin;
  };

  const calculatePrices = (cost, discount, vendor = null) => {
    // if (!discount || discount < 0)
    //   return {
    //     listPrice: 0,
    //     endUserPrice: 0,
    //     dealerPrice: 0,
    //     wholesalerPrice: 0,
    //     endUserMarkup: 1,
    //     dealerMarkup: 1,
    //     wholesalerMarkup: 1,
    //   };

    const listPrice = calcListPrice(cost, discount);

    return {
      listPrice: listPrice,
      endUserPrice: calcEndUserPrice(listPrice, discount, vendor),
      dealerPrice: calcDealerPrice(listPrice, discount, vendor),
      wholesalerPrice: calcWholesalerPrice(listPrice, discount, vendor),
      endUserMarkup: calcEndUserMarkup(discount, vendor),
      dealerMarkup: calcDealerMarkup(discount, vendor),
      wholesalerMarkup: calcWholesalerMarkup(discount, vendor),
    };
  };

  return {
    calcEndUserMarkup,
    calcDealerMarkup,
    calcWholesalerMarkup,
    calcEndUserPrice,
    calcDealerPrice,
    calcWholesalerPrice,
    calcCost,
    calcListPrice,
    calcMargin,
    calculatePrices,
    setMarkupData: setMarkups,
  };
};

export default useMarkups;
